/**
 * /app/programmes — the patient's owned online programmes.
 *
 * Displays every purchased 6-week self-guided programme with a download
 * button that streams the PDF via `GET /api/programmes/:id/download`.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, BookOpen, Download, FileText, Loader2, Plus, Trash2 } from "lucide-react";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";

export default function PatientProgrammes() {
  const [items, setItems] = useState(null);   // null = loading
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    api.get("/online-programmes/mine")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  const download = async (programmeId, name) => {
    setDownloading((d) => ({ ...d, [programmeId]: true }));
    try {
      const r = await api.get(`/online-programmes/${programmeId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name || programmeId}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed — please try again.");
    } finally {
      setDownloading((d) => ({ ...d, [programmeId]: false }));
    }
  };

  return (
    <DashLayout>
      <div className="mb-6">
        <div className="fl-label text-[color:var(--fl-orange)]">Self-guided</div>
        <h1 className="fl-heading text-3xl md:text-4xl mt-2" data-testid="patient-programmes-heading">My programmes</h1>
        <p className="text-sm mt-2" style={{ color: "var(--fl-muted)" }}>
          Every 6-week programme you've unlocked lives here. Download the PDF or open it directly on any device.
        </p>
      </div>

      {items === null && (
        <div className="fl-card p-8 text-center text-sm text-zinc-400" data-testid="patient-programmes-loading">
          <Loader2 className="animate-spin mx-auto mb-2" /> Loading…
        </div>
      )}
      {items && items.length === 0 && (
        <div className="fl-card p-8 text-center" data-testid="patient-programmes-empty">
          <div className="fl-heading text-lg">No programmes yet</div>
          <p className="text-sm mt-2" style={{ color: "var(--fl-muted)" }}>
            When you purchase a 6-week self-guided programme, it will show up here.
          </p>
          <Link to="/prices#online-programmes" className="fl-btn mt-5 inline-flex" data-testid="patient-programmes-cta-shop">
            Browse online programmes <ArrowRight size={13} />
          </Link>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="grid gap-4" data-testid="patient-programmes-list">
          {items.map((p) => (
            <ProgrammeCard
              key={`${p.programme_id}-${p.granted_at}`}
              p={p}
              downloading={downloading[p.programme_id]}
              onDownload={() => download(p.programme_id, p.original_filename?.replace(/\.pdf$/i, "") || p.name)}
            />
          ))}
        </div>
      )}
    </DashLayout>
  );
}

function ProgrammeCard({ p, downloading, onDownload }) {
  const [entries, setEntries] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ note: "", mood: "" });
  const weekNow = useMemo(() => {
    const days = Math.max(0, Math.floor((Date.now() - new Date(p.granted_at).getTime()) / (1000 * 60 * 60 * 24)));
    return Math.min(6, Math.max(1, Math.floor(days / 7) + 1));
  }, [p.granted_at]);
  const [week, setWeek] = useState(weekNow);

  const load = async () => {
    try {
      const r = await api.get(`/online-programmes/${p.programme_id}/journal`);
      setEntries(r.data || []);
    } catch {
      setEntries([]);
    }
  };
  useEffect(() => { if (open && entries === null) load(); }, [open]);

  const submit = async () => {
    if (!draft.note.trim()) { toast.error("Add a short note first."); return; }
    setSaving(true);
    try {
      const r = await api.post(`/online-programmes/${p.programme_id}/journal`, {
        week, note: draft.note, mood: draft.mood || undefined,
      });
      setEntries((cur) => [r.data, ...(cur || [])]);
      setDraft({ note: "", mood: "" });
      toast.success(`Week ${week} note saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/online-programmes/${p.programme_id}/journal/${id}`);
      setEntries((cur) => (cur || []).filter((x) => x.id !== id));
    } catch { toast.error("Could not delete."); }
  };

  return (
    <div className="fl-card p-5" data-testid={`patient-programmes-row-${p.programme_id}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 fl-label text-[color:var(--fl-orange)]">
            <FileText size={12} /> Self-guided programme · Week {weekNow} of 6
          </div>
          <div className="fl-heading text-xl mt-1">{p.name}</div>
          {p.headline && <div className="text-sm mt-1" style={{ color: "var(--fl-muted)" }}>{p.headline}</div>}
          <div className="text-[11px] mt-2" style={{ color: "var(--fl-muted-2, var(--fl-muted))" }}>
            Unlocked {new Date(p.granted_at).toLocaleDateString()} · {p.duration || "6 weeks"}
          </div>
        </div>
        <div className="shrink-0 flex flex-col gap-2 md:min-w-[220px]">
          {p.has_pdf ? (
            <button
              type="button" onClick={onDownload} disabled={!!downloading}
              className="fl-btn text-sm justify-center"
              data-testid={`patient-programmes-download-${p.programme_id}`}
            >
              {downloading ? (<><Loader2 size={14} className="animate-spin" /> Preparing…</>)
                           : (<><Download size={14} /> Download PDF</>)}
            </button>
          ) : (
            <div className="text-[11px] text-amber-300/80 text-right">
              Your PDF is on the way — we'll email it as soon as it's ready.
            </div>
          )}
          <button
            type="button" onClick={() => setOpen((v) => !v)}
            className="fl-btn-anim fl-btn-ghost text-xs justify-center"
            data-testid={`patient-programmes-journal-toggle-${p.programme_id}`}
          >
            <BookOpen size={13} /> {open ? "Hide journal" : "Open journal"}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--fl-border)" }}
             data-testid={`patient-programmes-journal-${p.programme_id}`}>
          <div className="fl-label text-[color:var(--fl-orange)] mb-2">Weekly journal</div>
          <div className="grid md:grid-cols-[110px_110px_1fr_auto] gap-2 items-start">
            <select className="fl-input" value={week} onChange={(e) => setWeek(Number(e.target.value))}
                    data-testid={`journal-week-${p.programme_id}`}>
              {[1, 2, 3, 4, 5, 6].map((w) => <option key={w} value={w}>Week {w}</option>)}
            </select>
            <input className="fl-input" placeholder="Mood (e.g. 💪)" maxLength={40}
                   value={draft.mood} onChange={(e) => setDraft({ ...draft, mood: e.target.value })}
                   data-testid={`journal-mood-${p.programme_id}`} />
            <textarea className="fl-input" rows={2} placeholder="How did this week go? What felt easier or harder?"
                      value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                      data-testid={`journal-note-${p.programme_id}`} />
            <button type="button" onClick={submit} disabled={saving}
                    className="fl-btn text-xs justify-center h-fit"
                    data-testid={`journal-submit-${p.programme_id}`}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Save
            </button>
          </div>

          {entries === null && (
            <div className="text-xs mt-4 text-zinc-500"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading…</div>
          )}
          {entries && entries.length === 0 && (
            <div className="text-xs mt-4 text-zinc-500 italic">No entries yet — jot down a short note each week so you can look back at your arc.</div>
          )}
          {entries && entries.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid={`journal-list-${p.programme_id}`}>
              {entries.map((e) => (
                <li key={e.id} className="fl-card p-3 flex items-start gap-3" data-testid={`journal-entry-${e.id}`}>
                  <div className="fl-label text-[color:var(--fl-orange)] shrink-0 mt-0.5">W{e.week}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-zinc-500">
                      {new Date(e.created_at).toLocaleDateString()} {e.mood ? `· ${e.mood}` : ""}
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{e.note}</div>
                  </div>
                  <button type="button" onClick={() => remove(e.id)}
                          className="text-zinc-500 hover:text-red-400 px-1"
                          data-testid={`journal-delete-${e.id}`}
                          title="Delete"><Trash2 size={13} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
